import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Banknote, QrCode, CreditCard, Landmark, Printer, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rp, computeTotals } from "@/lib/api";
import { TotalsBlock } from "@/components/BillItems";
import { printReceipt } from "@/components/Receipt";
import { useAuth } from "@/context/AuthContext";

const METHODS = [
  { id: "tunai", label: "Tunai", icon: Banknote },
  { id: "qris", label: "QRIS", icon: QrCode },
  { id: "kartu", label: "Kartu", icon: CreditCard },
  { id: "transfer", label: "Transfer", icon: Landmark },
];

export default function PaymentDialog({ bill: initial, open, onOpenChange, onPaid }) {
  const { settings } = useAuth();
  const [method, setMethod] = useState("tunai");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [dtype, setDtype] = useState("nominal");
  const [dvalue, setDvalue] = useState("");
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(null);
  const [fresh, setFresh] = useState(null);
  const bill = fresh && fresh.id === initial?.id ? fresh : initial;

  useEffect(() => {
    if (open && initial) {
      setMethod("tunai");
      setAmount("");
      setReference("");
      setDtype("nominal");
      setDvalue("");
      setPaid(null);
      api.get(`/bills/${initial.id}`).then((r) => setFresh(r.data)).catch(() => {});
    }
  }, [open, initial]);

  if (!bill) return null;
  const totals = settings ? computeTotals(bill.totals.subtotal, settings, dtype, dvalue) : bill.totals;
  const total = totals.total;
  const paidAmount = method === "tunai" ? Number(amount || 0) : total;
  const change = paidAmount - total;
  const quick = [total, Math.ceil(total / 50000) * 50000, Math.ceil(total / 100000) * 100000].filter((v, i, a) => a.indexOf(v) === i);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bills/${bill.id}/pay`, { method, amount_paid: paidAmount, reference, discount_type: dtype, discount_value: Number(dvalue || 0) });
      setPaid(r.data);
      toast.success("Pembayaran berhasil!");
      onPaid?.(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Pembayaran <span className="font-mono text-orange-700">{bill.code}</span></DialogTitle>
        </DialogHeader>
        {paid ? (
          <div className="space-y-4 text-center" data-testid="payment-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <p className="text-lg font-bold">Lunas</p>
            <p className="text-sm text-zinc-600">Kembalian: <span className="font-mono font-bold" data-testid="payment-change-result">{rp(paid.payment.change)}</span></p>
            <Button data-testid="payment-print-btn" className="w-full bg-orange-600 hover:bg-orange-700" onClick={() => printReceipt(paid, settings)}>
              <Printer className="mr-2 h-4 w-4" /> Cetak Struk
            </Button>
            <Button data-testid="payment-close-btn" variant="outline" className="w-full" onClick={() => onOpenChange(false)}>Tutup</Button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2" data-testid="discount-box">
              <Label>Diskon</Label>
              <div className="flex gap-2">
                <div className="flex overflow-hidden rounded-md border border-stone-200">
                  {[["nominal", "Rp"], ["persen", "%"]].map(([k, l]) => (
                    <button key={k} type="button" data-testid={`discount-type-${k}`} onClick={() => setDtype(k)} className={`px-3 text-sm font-semibold ${dtype === k ? "bg-orange-600 text-white" : "bg-white text-zinc-600"}`}>{l}</button>
                  ))}
                </div>
                <Input data-testid="discount-value-input" type="number" inputMode="numeric" min="0" max={dtype === "persen" ? 100 : undefined} value={dvalue} onChange={(e) => setDvalue(e.target.value)} placeholder={dtype === "persen" ? "0 - 100" : "0"} className="font-mono" />
              </div>
            </div>
            <div className="rounded-xl bg-stone-50 p-4"><TotalsBlock totals={totals} testId="payment-totals" /></div>
            <div className="grid grid-cols-4 gap-2">
              {METHODS.map((m) => (
                <button key={m.id} type="button" data-testid={`pay-method-${m.id}`} onClick={() => setMethod(m.id)}
                  className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-xs font-semibold transition-colors active:scale-95 ${method === m.id ? "border-orange-600 bg-orange-50 text-orange-700" : "border-stone-200 text-zinc-600 hover:bg-stone-50"}`}>
                  <m.icon className="h-5 w-5" />{m.label}
                </button>
              ))}
            </div>
            {method === "tunai" ? (
              <div className="space-y-2">
                <Label>Uang diterima</Label>
                <Input data-testid="pay-amount-input" type="number" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className="font-mono text-lg" />
                <div className="flex flex-wrap gap-2">
                  {quick.map((q) => (
                    <button key={q} type="button" data-testid={`pay-quick-${q}`} onClick={() => setAmount(String(q))} className="rounded-full border border-stone-200 px-3 py-1 font-mono text-xs hover:bg-orange-50">{rp(q)}</button>
                  ))}
                </div>
                <p className="text-sm">Kembalian: <span data-testid="pay-change" className={`font-mono font-bold ${change < 0 ? "text-red-600" : "text-emerald-700"}`}>{change < 0 ? `Kurang ${rp(-change)}` : rp(change)}</span></p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>No. referensi (opsional)</Label>
                <Input data-testid="pay-reference-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Contoh: 4 digit akhir kartu / ID transaksi" />
                <p className="text-xs text-zinc-500">Pastikan pembayaran {rp(total)} sudah diterima sebelum konfirmasi.</p>
              </div>
            )}
            <Button data-testid="pay-confirm-btn" disabled={busy || change < 0} onClick={submit} className="h-12 w-full bg-orange-600 text-base hover:bg-orange-700 active:scale-95">
              {busy ? "Memproses..." : `Konfirmasi Bayar ${rp(total)}`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
